import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ButtonComponent } from '@modules/components/button/button.component';
import { SnackBarComponent } from '@modules/components/snack-bar/snack-bar.component';
import { FullNamePipe } from '@modules/pipes/full-name.pipe';
import { UserType } from '@type/user.type';
import { startWith } from 'rxjs';

import { UsersService } from '../../services/users/users.service';

@Component({
  selector: 'app-copy-user-settings-dialog',
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatIconModule,
    ReactiveFormsModule,
    ButtonComponent,
    FullNamePipe,
  ],
  templateUrl: './copy-user-settings-dialog.component.html',
  styleUrls: ['./copy-user-settings-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopyUserSettingsDialogComponent {
  private dialogRef = inject(MatDialogRef<CopyUserSettingsDialogComponent>);

  private data = inject<{ companyId: number }>(MAT_DIALOG_DATA);

  private usersService = inject(UsersService);

  private snack = inject(MatSnackBar);

  private fb = inject(FormBuilder);

  loading = signal(true);

  allUsers = signal<UserType[]>([]);

  donor = signal<UserType | null>(null);

  selectedIds = signal(new Set<number>());

  recipientSearch = signal('');

  donorControl = this.fb.control<string | UserType | null>(null);

  donorControlValue = toSignal(this.donorControl.valueChanges.pipe(startWith(null)), { initialValue: null });

  filteredDonors = computed(() => {
    const value = this.donorControlValue();
    const search = typeof value === 'string' ? value.toLowerCase() : '';
    return this.allUsers().filter((u) => this.matchesSearch(u, search));
  });

  filteredRecipients = computed(() => {
    const donorId = this.donor()?.id;
    const search = this.recipientSearch().toLowerCase().trim();
    return this.allUsers().filter((u) => u.id !== donorId && this.matchesSearch(u, search));
  });

  isAllSelected = computed(() => {
    const filtered = this.filteredRecipients();
    if (!filtered.length) return false;
    const ids = this.selectedIds();
    return filtered.every((u) => ids.has(u.id));
  });

  isIndeterminate = computed(() => {
    const filtered = this.filteredRecipients();
    const ids = this.selectedIds();
    const count = filtered.filter((u) => ids.has(u.id)).length;
    return count > 0 && count < filtered.length;
  });

  selectedCount = computed(() => this.selectedIds().size);

  canCopy = computed(() => this.donor() !== null && this.selectedIds().size > 0);

  constructor() {
    this.usersService
      .getUsersList({ per_page: 10000, page: 1, q: {}, sorting: '' }, this.data.companyId)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: ({ data }) => {
          this.allUsers.set(data as UserType[]);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });

    effect(() => {
      if (!this.donor()) {
        this.recipientSearch.set('');
        this.selectedIds.set(new Set());
      }
    });
  }

  displayUser = (user: UserType | string | null): string => {
    if (!user || typeof user === 'string') return '';
    return `${user.first_name} ${user.last_name}`;
  };

  onDonorSelected(event: MatAutocompleteSelectedEvent): void {
    const user = event.option.value as UserType;
    this.donor.set(user);
    this.selectedIds.update((ids) => {
      const next = new Set(ids);
      next.delete(user.id);
      return next;
    });
  }

  clearDonor(event: MouseEvent): void {
    event.stopPropagation();
    this.donor.set(null);
    this.donorControl.reset(null);
  }

  toggleRecipient(userId: number): void {
    if (!this.donor()) return;
    this.selectedIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  toggleAll(): void {
    const filtered = this.filteredRecipients();
    if (this.isAllSelected()) {
      this.selectedIds.update((ids) => {
        const next = new Set(ids);
        filtered.forEach((u) => next.delete(u.id));
        return next;
      });
    } else {
      this.selectedIds.update((ids) => {
        const next = new Set(ids);
        filtered.forEach((u) => next.add(u.id));
        return next;
      });
    }
  }

  copy(): void {
    const donor = this.donor();
    if (!donor || !this.selectedIds().size) return;

    this.usersService.copyUserSettings(this.data.companyId, donor.id, [...this.selectedIds()]).subscribe({
      next: ({ succeeded_count, failed_count }) => {
        const message =
          failed_count === 0 ?
            `Settings copied to ${succeeded_count} member(s) successfully`
          : `Copied: ${succeeded_count}, failed: ${failed_count}`;
        this.snack.openFromComponent(SnackBarComponent, {
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          data: { message },
          duration: 5000,
        });
        this.dialogRef.close();
      },
    });
  }

  private matchesSearch(user: UserType, search: string): boolean {
    if (!search) return true;
    return (
      user.first_name?.toLowerCase().includes(search) ||
      user.last_name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  }
}
